import type { TOC } from '@ember/component/template-only';
import { t } from 'ember-intl';
import courseCashMark from 'tio-common/helpers/course-cash-mark';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import CardBalance from '../card-balance';
import CourseCashLegal from '../course-cash-legal';
import type { CourseCashTransaction } from 'tio-common/types/course-cash-transaction';

/**
 * One swipe-level row in the card body: an authorized swipe awaiting capture,
 * or a captured swipe whose receipt is uploaded/verified. Labels are
 * pre-formatted by the owner (needs-your-attention.gts) so this stays stateless.
 */
export interface CourseCashSwipeRow {
  transaction: CourseCashTransaction;
  amountLabel: string;
  dateLabel?: string;
  merchantLabel?: string;
  statusLabel: string;
  /** Receipt-status rows only — colors the status chip. */
  chipIntent?: 'warning' | 'success';
}

interface CourseCashBodySignature {
  Args: {
    institutionName?: string;
    availableBalance?: string;
    useByDate?: string;
    /** Card balance in DOLLARS (the shell converts the model's cents value). */
    cardBalance: number;
    last4?: string;
    /** Selects the post-use introduction. */
    used?: boolean;
    /** Pre-formatted amount spent, shown post-use. */
    spentAmount?: string;
    /** Pre-formatted amount remaining, shown post-use. */
    remainingAmount?: string;
    /** Authorized swipes awaiting capture; no action is available. */
    pendingCaptureRows?: CourseCashSwipeRow[];
    /** Uploaded/verified receipts with status and View access. */
    receiptStatusRows?: CourseCashSwipeRow[];
    onViewReceipt?: (transaction: CourseCashTransaction) => void;
  };
  Element: HTMLDivElement;
}

/**
 * Body of the "Use CourseCash" task: explanatory copy, the authorized/balance/use-by
 * detail rows, and the card visual. The "Card Details" CTA lives in the task
 * card's header (matching the legacy show page), not here — see
 * `needs-your-attention.gts`'s `<:headerActions>` block.
 */
const CourseCashBody: TOC<CourseCashBodySignature> = <template>
  <div class="tio-col gap-4" ...attributes>
    <p class="tio-text-sm-normal tio-fg-secondary">
      {{#if @used}}
        {{courseCashMark
          (t "tuition_assistance.applications.manage.tasks.use_course_cash_used_body")
        }}
      {{else}}
        {{t "tuition_assistance.applications.manage.tasks.use_course_cash_body"}}
      {{/if}}
    </p>

    <dl class="tio-col gap-1">
      {{#if @institutionName}}
        <div class="tio-row flex-wrap items-baseline gap-2">
          <dt class="tio-text-sm-medium tio-fg-secondary">
            {{t "tuition_assistance.applications.manage.tasks.authorized_at"}}
          </dt>
          <dd class="tio-text-sm-normal tio-fg-primary">
            {{@institutionName}}
            <span class="tio-text-sm-normal tio-fg-secondary italic">
              {{t "tuition_assistance.applications.manage.tasks.authorized_at_hint"}}
            </span>
          </dd>
        </div>
      {{/if}}
      {{#if @availableBalance}}
        <div class="tio-row flex-wrap items-baseline gap-2">
          <dt class="tio-text-sm-medium tio-fg-secondary">
            {{t "tuition_assistance.applications.manage.tasks.available_balance"}}
          </dt>
          <dd class="tio-text-sm-normal tio-fg-primary">
            {{@availableBalance}}
          </dd>
        </div>
      {{/if}}
      {{#if @spentAmount}}
        <div class="tio-row flex-wrap items-baseline gap-2">
          <dt class="tio-text-sm-medium tio-fg-secondary">
            {{t "tuition_assistance.applications.manage.tasks.spent_label"}}
          </dt>
          <dd class="tio-text-sm-normal tio-fg-primary" data-test-course-cash-spent>
            {{@spentAmount}}
          </dd>
        </div>
      {{/if}}
      {{#if @remainingAmount}}
        <div class="tio-row flex-wrap items-baseline gap-2">
          <dt class="tio-text-sm-medium tio-fg-secondary">
            {{t "tuition_assistance.applications.manage.tasks.remaining_label"}}
          </dt>
          <dd class="tio-text-sm-normal tio-fg-primary" data-test-course-cash-remaining>
            {{@remainingAmount}}
          </dd>
        </div>
      {{/if}}
      {{#if @useByDate}}
        <div class="tio-row flex-wrap items-baseline gap-2">
          <dt class="tio-text-sm-medium tio-fg-secondary">
            {{t "tuition_assistance.applications.manage.tasks.use_by_label"}}
          </dt>
          <dd class="tio-text-sm-normal tio-fg-primary">
            {{@useByDate}}
            <span class="tio-text-sm-normal tio-fg-secondary italic">
              {{t "tuition_assistance.applications.manage.tasks.use_by_hint"}}
            </span>
          </dd>
        </div>
      {{/if}}
    </dl>

    {{! Authorized swipes awaiting capture (#10290) — informational only, so no
        control: the swipe exists but the receipt pipeline starts at capture. }}
    {{#if @pendingCaptureRows.length}}
      <div class="tio-col gap-1">
        {{#each @pendingCaptureRows key="transaction.id" as |row|}}
          <div
            class="tio-row flex-wrap items-baseline gap-2"
            data-test-pending-capture-row={{row.transaction.id}}
          >
            {{#if row.dateLabel}}
              <span class="tio-text-sm-normal tio-fg-primary">{{row.dateLabel}}</span>
            {{/if}}
            {{#if row.merchantLabel}}
              <span class="tio-text-sm-normal tio-fg-primary">{{row.merchantLabel}}</span>
            {{/if}}
            <span class="tio-text-sm-normal tio-fg-primary">{{row.amountLabel}}</span>
            <span
              class="tio-text-sm-normal tio-fg-secondary italic"
              data-test-pending-capture-status
            >
              {{row.statusLabel}}
            </span>
          </div>
        {{/each}}
      </div>
    {{/if}}

    {{! Swipes whose receipt is already in (#10291) — the status chip plus the
        View entry point back into the receipt modal, which otherwise loses its
        only opener once the upload task clears. }}
    {{#if @receiptStatusRows.length}}
      <div class="tio-col gap-1">
        {{#each @receiptStatusRows key="transaction.id" as |row|}}
          <div
            class="tio-row flex-wrap items-baseline gap-2"
            data-test-receipt-status-row={{row.transaction.id}}
          >
            {{#if row.dateLabel}}
              <span class="tio-text-sm-normal tio-fg-primary">{{row.dateLabel}}</span>
            {{/if}}
            {{#if row.merchantLabel}}
              <span class="tio-text-sm-normal tio-fg-primary">{{row.merchantLabel}}</span>
            {{/if}}
            <span class="tio-text-sm-normal tio-fg-primary">{{row.amountLabel}}</span>
            <Chip @intent={{row.chipIntent}} @appearance="faded" @size="sm">
              {{row.statusLabel}}
            </Chip>
            {{#if @onViewReceipt}}
              <Button
                @appearance="minimal"
                @intent="primary"
                @size="sm"
                @testHook="manage-course-cash__view-receipt"
                {{on "click" (fn @onViewReceipt row.transaction)}}
                data-test-view-receipt={{row.transaction.id}}
              >
                {{t "tuition_assistance.applications.manage.tasks.view_receipt_cta"}}
              </Button>
            {{/if}}
          </div>
        {{/each}}
      </div>
    {{/if}}

    {{! Fee disclosure, sharing the legacy show card's key (stripe-card-display.gts)
        rather than a manage-local copy, so the covered percentage only ever has
        one place to change. Outside the <dl> because it is a note, not a
        term/definition pair — a bare <p> is not valid inside a dl. }}
    <p class="tio-text-xs-normal tio-fg-secondary italic" data-test-course-cash-fee-note>
      {{t "tas.stripe_details.any_merchant_transaction_fees"}}
    </p>

    <CardBalance @balance={{@cardBalance}} @last4={{@last4}} class="max-w-xs" />

    {{! Same pairing the drawer's Card Details tab carries — the card visual is
        shown here too, so the guidelines and the issuer disclosure belong with
        it rather than only behind the drawer. }}
    <CourseCashLegal @context="task-card" />
  </div>
</template>;

export default CourseCashBody;
